// app/VAi.js
import React, { useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  ActivityIndicator
} from 'react-native';
import axios from 'axios';
//import { OPENAI_API_KEY } from '../config';
import API_BASE_URL from '../apiConfig.js';

// Helper to fetch a token from localStorage
const getToken = () => {
  try {
    return localStorage.getItem('userToken');
  } catch {
    return null;
  }
};

// Helper: keep payloads compact (saves tokens & privacy)
const trimText = (s = '', n = 200) => (s.length > n ? `${s.slice(0, n)}…` : s);

export default function VAi() {
  const [input, setInput] = useState('');
  const [response, setResponse] = useState('');
  const [loading, setLoading] = useState(false);

  const handleQuery = async () => {
    if (!input.trim()) return;
    setLoading(true);
    setResponse('');

    try {
      const token = getToken();

      // Pull data in parallel with auth header
      const [customersRes, jobsRes, invoicesRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/customers`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API_BASE_URL}/jobs`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API_BASE_URL}/invoices`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      // Slim down the records before sending to the model
      const customers = (customersRes.data || [])
        .slice(0, 50)
        .map(({ id, name, city, state, zip, phone, email }) => ({
          id, name, city, state, zip, phone, email,
        }));

      const jobs = (jobsRes.data || [])
        .slice(0, 75)
        .map((j) => ({
          id: j.id,
          customerName: j.customerName,
          title: j.title,
          description: trimText(j.description, 220),
          price: j.price,
          selectedDate: j.selectedDate || j.date,  // API varies across screens
          selectedTime: j.selectedTime || j.time,
          city: j.city, state: j.state, zip: j.zip,
          completed: j.completed,
          visitType: j.visitType, // "job" | "quote"
        }));

      const invoices = (invoicesRes.data || [])
        .slice(0, 75)
        .map((inv) => ({
          id: inv.id,
          customer: inv.customer || inv.customerName,
          title: inv.title,
          description: trimText(inv.description, 200),
          price: inv.price,
          selectedDate: inv.selectedDate,
          selectedTime: inv.selectedTime,
          sentEmail: inv.sentEmail,
          sentText: inv.sentText,
        }));

      // Compose a compact, structured prompt
      const prompt = [
        `You are VAi, a precise assistant for a home-services business.`,
        `Answer clearly and concisely. If the question involves totals or dates, compute from the provided data.`,
        `If the data is insufficient, say what else is needed.`,
        ``,
        `User question: "${input.trim()}"`,
        ``,
        `Context (JSON):`,
        JSON.stringify(
          {
            meta: {
              customers_count: customers.length,
              jobs_count: jobs.length,
              invoices_count: invoices.length,
              note: 'Fields trimmed for privacy & token efficiency.',
            },
            customers,
            jobs,
            invoices,
          },
          null,
          0
        ),
      ].join('\n');

      // ⚠️ In production, proxy this via your backend
      const res = await axios.post(
        'https://api.openai.com/v1/chat/completions',
        {
          model: 'gpt-4',
          messages: [
            {
              role: 'system',
              content:
                'You are a helpful assistant for managing small business data. Prefer short, actionable answers. Show calculations when relevant.',
            },
            { role: 'user', content: prompt },
          ],
          temperature: 0.2,
        },
        {
          /*headers: {
            Authorization: `Bearer ${OPENAI_API_KEY}`,
            'Content-Type': 'application/json',
          },*/
        }
      );

      const text = res?.data?.choices?.[0]?.message?.content?.trim();
      setResponse(text || 'I could not generate a response.');
      setInput(''); // Clear the input after success
    } catch (error) {
      console.error('VAi error:', error);
      setResponse(
        error?.response?.data?.error?.message ||
          'Something went wrong. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const disabled = loading || !input.trim();

  return (
    <ScrollView contentContainerStyle={styles.container} keyboardShouldPersistTaps="handled">
      <Text style={styles.title}>Ask VAi</Text>

      <TextInput
        style={styles.input}
        placeholder="Ask a question or give a command (e.g., “What jobs are due this week?”, “Total outstanding invoices”, “Show quotes for Smith”)"
        value={input}
        onChangeText={setInput}
        multiline
      />

      <TouchableOpacity
        style={[styles.button, disabled && { opacity: 0.6 }]}
        onPress={handleQuery}
        disabled={disabled}
      >
        <Text style={styles.buttonText}>{loading ? 'Working…' : 'Submit'}</Text>
      </TouchableOpacity>

      {loading ? (
        <ActivityIndicator size="large" color="#007AFF" />
      ) : (
        !!response && <Text style={styles.response}>{response}</Text>
      )}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    padding: 20,
    backgroundColor: '#fff',
    flexGrow: 1
  },
  title: {
    fontSize: 22,
    fontWeight: 'bold',
    marginBottom: 16,
    textAlign: 'center'
  },
  input: {
    borderWidth: 1,
    borderColor: '#ccc',
    padding: 12,
    borderRadius: 6,
    marginBottom: 16,
    minHeight: 100,
    textAlignVertical: 'top'
  },
  button: {
    backgroundColor: '#007AFF',
    padding: 12,
    borderRadius: 6,
    alignItems: 'center',
    marginBottom: 20
  },
  buttonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: 'bold'
  },
  response: {
    fontSize: 16,
    color: '#333',
    lineHeight: 22
  }
});
